import React, { useState, useEffect } from 'react';
import { Home, LayoutGrid, ShoppingCart, User, Clock, MapPin } from 'lucide-react';
import '../styles/GlobalHeader.css';

/**
 * Глобальный header
 *
 * Фичи:
 * - Градиентный логотип "🐟 Путь рыбы"
 * - Sticky позиция с backdrop-filter (опционально)
 * - Desktop navigation
 * - Локация или таймер (переключается при скролле)
 */
const GlobalHeader = ({
  location = "Москва и МО",
  // Desktop navigation props
  currentPage = 'home',
  onNavigate = null,
  cartItemsCount = 0,
  // Timer props (для sticky переключения)
  showTimer = false,
  closeDate = null
}) => {
  const [isSticky, setIsSticky] = useState(false);
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0 });

  // Расчёт оставшегося времени
  useEffect(() => {
    if (!closeDate) return;

    const calculateTimeLeft = () => {
      const now = new Date();
      const diff = closeDate - now;

      if (diff <= 0) {
        return { days: 0, hours: 0, minutes: 0 };
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      return { days, hours, minutes };
    };

    setTimeLeft(calculateTimeLeft());

    // Обновляем каждую минуту
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 60000);

    return () => clearInterval(timer);
  }, [closeDate]);

  // Sticky эффект при скролле
  useEffect(() => {
    const handleScroll = () => {
      setIsSticky(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header className={`global-header ${isSticky ? 'sticky' : ''}`}>
      {/* Топ-бар: Логотип + Навигация (Desktop) + Локация */}
      <div className="header-top">
        <div className="logo-container">
          <img
            src={new URL('../images/logo_fish.webp', import.meta.url).href}
            alt="Путь рыбы"
            className="logo-fish"
          />
        </div>

        {/* Desktop Navigation (скрыта на mobile) */}
        {onNavigate && (
          <nav className="desktop-nav">
            <button
              className={`desktop-nav-item ${currentPage === 'home' ? 'active' : ''}`}
              onClick={() => onNavigate('home')}
              aria-current={currentPage === 'home' ? 'page' : undefined}
            >
              <Home size={20} strokeWidth={2} />
              <span className="nav-label">Главная</span>
            </button>

            <button
              className={`desktop-nav-item ${currentPage === 'catalog' ? 'active' : ''}`}
              onClick={() => onNavigate('catalog')}
              aria-current={currentPage === 'catalog' ? 'page' : undefined}
            >
              <LayoutGrid size={20} strokeWidth={2} />
              <span className="nav-label">Каталог</span>
            </button>

            <button
              className={`desktop-nav-item ${currentPage === 'cart' ? 'active' : ''}`}
              onClick={() => onNavigate('cart')}
              aria-current={currentPage === 'cart' ? 'page' : undefined}
            >
              <div className="nav-icon-wrapper">
                <ShoppingCart size={20} strokeWidth={2} />
                {cartItemsCount > 0 && (
                  <span className="cart-badge">{cartItemsCount}</span>
                )}
              </div>
              <span className="nav-label">Корзина</span>
            </button>

            <button
              className={`desktop-nav-item ${currentPage === 'profile' ? 'active' : ''}`}
              onClick={() => onNavigate('profile')}
              aria-current={currentPage === 'profile' ? 'page' : undefined}
            >
              <User size={20} strokeWidth={2} />
              <span className="nav-label">Профиль</span>
            </button>
          </nav>
        )}

        {/* Таймер или локация — условный рендеринг */}
        <div className="header-right-slot">
          {showTimer && closeDate ? (
            <div className="header-timer visible">
              <Clock className="timer-icon" size={14} strokeWidth={2} />
              <span className="timer-text">
                {timeLeft.days > 0 && `${timeLeft.days}д `}
                {timeLeft.hours}ч {timeLeft.minutes.toString().padStart(2, '0')}м
              </span>
            </div>
          ) : showTimer && !closeDate ? (
            <div className="header-timer visible">
              <Clock className="timer-icon" size={14} strokeWidth={2} />
              <span className="timer-text">Скоро</span>
            </div>
          ) : (
            <div className="location">
              <MapPin className="location-icon" size={14} strokeWidth={2} />
              <span className="location-text">{location}</span>
            </div>
          )}
        </div>
      </div>

    </header>
  );
};

export default React.memo(GlobalHeader);
